/**
 * Plating add-on cart handling.
 *
 * The PDP shows a "Plating" selector (silver/rhodium = included, plus paid
 * options like 18kt Gold and Rose Gold). When a PAID plating is selected, Add
 * to Cart / Buy Now bundle a separate Shopify line item (the plating add-on
 * variant) so cart totals match Stripe, exactly like the original gold-plating
 * flow. The add-on line carries `_Service` so the Offer Engine excludes it
 * (value-agnostic). The main line carries a visible marker (e.g. "18kt Gold
 * plating: Yes") for the cart/order.
 *
 * The selected plating is read from the checked radio in the plating group;
 * each radio carries data-plating-key / -variant-id / -marker / -service.
 * Backward compatible: if the legacy single checkbox is present instead, the
 * old gold-only behaviour still works.
 *
 * Exposes window.GoldPlatingCart (name kept for buy-now-international.js) and
 * window.PlatingCart (alias).
 */
(function () {
  'use strict';

  function cartAddJsUrl() {
    var u = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add';
    if (/\.js(\?|$)/i.test(u)) return u;
    return u.replace(/\/cart\/add\/?(\?|$)/i, '/cart/add.js$1');
  }

  /**
   * Resolve the selected paid plating for a form, or null when none (silver /
   * no selector / legacy-unchecked). Shape: {variantId, marker, service}.
   */
  function selectedPlating(form) {
    if (!form || !form.querySelector) return null;

    // Preferred: radio plating group. On the PDP the selector is its own block
    // (beside the form), so fall back to the enclosing product section; inline
    // quick-view/featured keeps the radios inside the form.
    var checked = form.querySelector('input[name="plating_option"]:checked');
    if (!checked) {
      var scope = form.closest('[data-section-type]') || form.closest('.shopify-section');
      if (scope) checked = scope.querySelector('input[name="plating_option"]:checked');
    }
    if (checked) {
      var key = String(checked.getAttribute('data-plating-key') || '').trim();
      var variantId = String(checked.getAttribute('data-plating-variant-id') || '').trim();
      // Silver / rhodium (included) or any option without an add-on variant.
      if (key === 'silver' || !variantId) return null;
      return {
        variantId: variantId,
        marker: String(checked.getAttribute('data-plating-marker') || '').trim(),
        service: String(checked.getAttribute('data-plating-service') || 'plating').trim(),
      };
    }

    // Legacy fallback: single gold-plating checkbox + form-level variant id.
    var cb = form.querySelector('.product__gold-plating input[type="checkbox"]');
    if (cb && cb.checked) {
      var legacyId = String(form.getAttribute('data-gold-plating-addon-variant-id') || '').trim();
      if (legacyId) {
        return { variantId: legacyId, marker: '18kt Gold plating', service: '18kt gold plating' };
      }
    }
    return null;
  }

  /** True when the form has a paid plating selected (used by buy-now/add). */
  function isGoldPlatingChecked(form) {
    return !!selectedPlating(form);
  }

  /** Kept for API compatibility: the selected add-on variant id, or ''. */
  function getAddonVariantId(form) {
    var p = selectedPlating(form);
    return p ? p.variantId : String((form && form.getAttribute && form.getAttribute('data-gold-plating-addon-variant-id')) || '').trim();
  }

  function buildMainLineItem(form, opts) {
    opts = opts || {};
    var fd = new FormData(form);
    var id = fd.get('id');
    if (!id) throw new Error('Variant ID required');
    var quantity = parseInt(String(fd.get('quantity') || '1'), 10) || 1;
    var properties = {};
    fd.forEach(function (value, key) {
      if (typeof value !== 'string' || value === '') return;
      var m = /^properties\[(.+)\]$/.exec(key);
      if (m) properties[m[1]] = value;
    });
    // Visible marker on the main line, e.g. "18kt Gold plating: Yes".
    if (opts.platingMarker) properties[opts.platingMarker] = 'Yes';
    var item = { id: Number(id), quantity: quantity };
    if (Object.keys(properties).length) item.properties = properties;
    var sp = fd.get('selling_plan');
    if (sp) {
      var n = Number(sp);
      if (n) item.selling_plan = n;
    }
    return item;
  }

  /** Returns JSON string for POST body, or null if no add-on should be bundled. */
  function buildMultiItemBody(form) {
    var plating = selectedPlating(form);
    if (!plating) return null;
    var main = buildMainLineItem(form, { platingMarker: plating.marker });
    var items = [
      main,
      {
        id: Number(plating.variantId),
        quantity: main.quantity,
        properties: { _Service: plating.service },
      },
    ];
    return JSON.stringify({ items: items });
  }

  function postJsonAdd(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
      body: body,
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok || (data && data.status)) {
          var err = new Error(
            (data && (data.description || data.message)) || 'Failed to add to cart'
          );
          err.shopify = data || { message: 'Failed to add to cart' };
          throw err;
        }
        return data;
      });
    });
  }

  function showFormError(form, message) {
    var holder = (form.closest && form.closest('.shopify-section')) || form;
    var errBox = holder.querySelector('[data-cart-errors-container]');
    if (!errBox) return;
    var safe = String(message || 'Error').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    errBox.innerHTML =
      '<div class="errors">' +
      safe +
      '<button type="button" class="errors__close" data-close-error aria-label="Close"><svg aria-hidden="true" focusable="false" role="presentation" width="24" height="24" stroke-width="1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor" class="icon icon-cancel"><path d="M6.758 17.243L12.001 12m5.243-5.243L12 12m0 0L6.758 6.757M12.001 12l5.243 5.243" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"></path></svg></button></div>';
    errBox.classList.add('is-visible');
    var closeBtn = errBox.querySelector('[data-close-error]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        errBox.classList.remove('is-visible');
        errBox.innerHTML = '';
      });
    }
  }

  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target && e.target.closest && e.target.closest('button[name="add"][data-add-to-cart]');
      if (!btn || btn.disabled) return;
      var form = btn.closest('form.product-form');
      if (!form) return;
      var body = buildMultiItemBody(form);
      if (!body) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      var errClear = form.closest('.shopify-section') || form;
      var errBox = errClear.querySelector('[data-cart-errors-container]');
      if (errBox) {
        errBox.classList.remove('is-visible');
        errBox.innerHTML = '';
      }

      btn.classList.add('is-loading');
      btn.setAttribute('disabled', 'disabled');

      postJsonAdd(cartAddJsUrl(), body)
        .then(function (data) {
          btn.dispatchEvent(
            new CustomEvent('theme:product:add', {
              bubbles: true,
              detail: { response: data, button: btn },
            })
          );
          if (window.theme && window.theme.settings && window.theme.settings.cartType === 'page') {
            window.location = window.theme.routes.cart_url;
            return;
          }
          document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
        })
        .catch(function (err) {
          showFormError(form, (err.shopify && (err.shopify.description || err.shopify.message)) || err.message);
          btn.dispatchEvent(
            new CustomEvent('theme:product:add-error', {
              bubbles: true,
              detail: { response: err.shopify || { message: err.message }, button: btn },
            })
          );
        })
        .finally(function () {
          btn.classList.remove('is-loading');
          btn.removeAttribute('disabled');
        });
    },
    true
  );

  function cartLinePropertiesToPlainObject(item) {
    var p = item && item.properties;
    if (!p) return {};
    if (Array.isArray(p)) {
      var o = {};
      p.forEach(function (x) {
        if (x && x.name != null) o[x.name] = x.value;
      });
      return o;
    }
    if (typeof p === 'object') return p;
    return {};
  }

  /** JSON string for URLSearchParams (it encodes); empty if no properties. */
  function cartLinePropertiesJsonForUrl(item) {
    try {
      var o = cartLinePropertiesToPlainObject(item);
      if (!Object.keys(o).length) return '';
      return JSON.stringify(o);
    } catch (e) {
      return '';
    }
  }

  window.CartLineItemProperties = {
    toPlainObject: cartLinePropertiesToPlainObject,
    jsonForUrlParam: cartLinePropertiesJsonForUrl,
  };

  var api = {
    cartAddJsUrl: cartAddJsUrl,
    getAddonVariantId: getAddonVariantId,
    isGoldPlatingChecked: isGoldPlatingChecked,
    selectedPlating: selectedPlating,
    buildMultiItemBody: buildMultiItemBody,
    buildMainLineItem: buildMainLineItem,
    postJsonAdd: postJsonAdd,
  };
  window.GoldPlatingCart = api; // name kept for buy-now-international.js
  window.PlatingCart = api;
})();
